/**
 * Determinism primitives (Spike B). Engines must produce identical bytes in the
 * browser and the Worker. The classic divergence sources are object-key order and
 * float formatting. These helpers remove both.
 */

/**
 * Canonical JSON: object keys sorted recursively, so two structurally-equal values
 * always serialize to the exact same string. Use this for hashing, audit records,
 * and any cross-runtime byte comparison. Rejects non-finite numbers (a float bug
 * that must never reach a graded output).
 */
export function stableStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new Error('stableStringify: non-finite number is not serializable');
    }
    return value;
  }
  if (Array.isArray(value)) return value.map(canonicalize);
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    out[key] = canonicalize(obj[key]);
  }
  return out;
}

/**
 * FNV-1a 32-bit hash of a string — deterministic, no platform math, no deps.
 * Used to derive stable ids/seeds from canonical content without a crypto dep.
 */
export function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts, kept in uint32.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash >>> 0;
}
